// Utility functions
import type { ProcessingType } from '@/types';
import { ValidationError } from './logger';

export { cn } from './cn';
export { logger, ValidationError, NetworkError, FileSizeError, ProcessingError, PaymentError } from './logger';

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

type CostOptions =
  | boolean
  | {
      isSubscriber?: boolean;
      aiOverview?: boolean;
    };

/**
 * Calculate estimated cost for batch processing using tiered pricing
 * Tier 1: 5-24 rows (small batch premium)
 * Tier 2: 25-499 rows (standard rate)
 * Tier 3: 500+ rows (volume discount)
 */
export function calculateEstimatedCost(
  rowCount: number,
  processingType: ProcessingType,
  options: CostOptions = false
): number {
  if (rowCount < 0) {
    throw new ValidationError(`Invalid row count: ${rowCount}. Row count must be positive`);
  }

  const aiOverview =
    typeof options === 'object' && options !== null && !Array.isArray(options)
      ? options.aiOverview ?? false
      : false;

  // Tiered pricing structure (matching BATCH_PRICING in config/pricing.ts)
  const BATCH_PRICING = {
    validate: {
      tier1: 0.18, // 5-24 rows
      tier2: 0.15, // 25-499 rows
      tier3: 0.12, // 500+ rows
    },
    enrich: {
      tier1: 0.04,
      tier2: 0.03,
      tier3: 0.025,
    },
    enrich_validate: {
      tier1: 0.20,
      tier2: 0.18,
      tier3: 0.15,
    },
    scoring: {
      tier1: 0.55,
      tier2: 0.48,
      tier3: 0.42,
    },
    scoring_enrich: {
      tier1: 0.60,
      tier2: 0.52,
      tier3: 0.45,
    },
  };

  if (!BATCH_PRICING[processingType]) {
    throw new ValidationError(`Invalid processing type for cost calculation: ${processingType}. Valid types: ${Object.keys(BATCH_PRICING).join(', ')}`);
  }

  // Determine tier based on row count
  let rate: number;
  if (rowCount < 25) {
    rate = BATCH_PRICING[processingType].tier1;
  } else if (rowCount < 500) {
    rate = BATCH_PRICING[processingType].tier2;
  } else {
    rate = BATCH_PRICING[processingType].tier3;
  }

  // Add AI Overview cost if applicable
  if (aiOverview && (processingType === 'scoring' || processingType === 'scoring_enrich')) {
    rate += 0.03; // AI overview add-on (updated from $0.01 to $0.03)
  }

  return Number((rowCount * rate).toFixed(2));
}

export function generateJobId(): string {
  return `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function generateSessionId(): string {
  return `cs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getProcessingTypeDescription(type: ProcessingType): string {
  const descriptions = {
    validate: 'Identity validation - verify names, emails, and phone numbers',
    enrich: 'Data enrichment - enhance contact information and addresses',
    enrich_validate: 'Contact enrichment with identity verification',
    scoring: 'AI-powered collectability scoring and recovery probability',
    scoring_enrich: 'Complete scoring with data enrichment'
  };
  
  return descriptions[type] || '';
}

export function validateCSVStructure(headers: string[]): { isValid: boolean; errors: string[] } {
  const requiredHeaders = ['name', 'email', 'phone', 'amount_to_collect'];
  const optionalHeaders = ['address', 'date_of_birth', 'ssn_last_4'];
  const errors: string[] = [];

  // Check required headers
  const missingRequired = requiredHeaders.filter(header => 
    !headers.some(h => h.toLowerCase().trim() === header.toLowerCase())
  );

  if (missingRequired.length > 0) {
    errors.push(`Missing required columns: ${missingRequired.join(', ')}`);
  }

  // Check for unknown headers
  const allValidHeaders = [...requiredHeaders, ...optionalHeaders];
  const unknownHeaders = headers.filter(header => 
    !allValidHeaders.some(valid => valid.toLowerCase() === header.toLowerCase().trim())
  );

  if (unknownHeaders.length > 0) {
    errors.push(`Unknown columns found: ${unknownHeaders.join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

export function simulateProcessingDelay(processingType: ProcessingType, rowCount: number): number {
  if (rowCount < 0) {
    throw new ValidationError(`Invalid row count for processing delay: ${rowCount}. Row count must be positive`);
  }

  // Base processing time per row (in milliseconds)
  const baseTimes = {
    validate: 50,
    enrich: 75,
    enrich_validate: 125,
    scoring: 100,
    scoring_enrich: 175
  };

  if (!baseTimes[processingType]) {
    throw new ValidationError(`Invalid processing type for delay calculation: ${processingType}. Valid types: ${Object.keys(baseTimes).join(', ')}`);
  }

  const baseTime = baseTimes[processingType];
  const totalTime = Math.max(2000, rowCount * baseTime); // Minimum 2 seconds
  
  return Math.min(totalTime, 30000); // Maximum 30 seconds for demo
}
